import React from 'react';
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog';
import { List, ListItem, ListItemText, Divider, Typography } from '@material-ui/core';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useGetStockTransactionItemsQuery, StockTransactionType } from 'generated/graphql';
import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';

interface StockTransactionItemsDialogProps {
	open: boolean;
	onClose: () => void;
	transactionID: string | null;
	transactionType: StockTransactionType | null;
}

export const StockTransactionItemsDialog: React.FC<StockTransactionItemsDialogProps> = ({
	open,
	onClose,
	transactionID,
	transactionType,
}) => {
	const { data, loading, error } = useGetStockTransactionItemsQuery({
		variables: {
			transactionID: transactionID || '',
			transactionType: (transactionType || StockTransactionType.StockIn) as StockTransactionType,
		},
		skip: !transactionID || !transactionType || !open,
		fetchPolicy: 'network-only',
	});

	const getTransactionTypeLabel = (type: StockTransactionType | null) => {
		switch (type) {
			case StockTransactionType.StockIn:
				return 'Stock In';
			case StockTransactionType.StockOut:
				return 'Stock Out';
			case StockTransactionType.StockAdjustment:
				return 'Stock Adjustment';
			default:
				return 'Transaction';
		}
	};

	const getQuantityLabel = (type: StockTransactionType | null) => {
		switch (type) {
			case StockTransactionType.StockIn:
				return 'Qty Received';
			case StockTransactionType.StockOut:
				return 'Transfer Qty';
			case StockTransactionType.StockAdjustment:
				return 'Qty Adjusted';
			default:
				return 'Quantity';
		}
	};

	const getQuantityValue = (item: any, type: StockTransactionType | null) => {
		switch (type) {
			case StockTransactionType.StockIn:
				return item.qtyReceived;
			case StockTransactionType.StockOut:
				return item.transferQty;
			case StockTransactionType.StockAdjustment:
				return item.qtyAdjusted;
			default:
				return null;
		}
	};

	const items = data?.getStockTransactionItems || [];

	return (
		<CommonDialog
			fullWidth
			maxWidth="md"
			open={open}
			onClose={onClose}
			sections={{
				header: {
					dynamic: (
						<div className="dialog-dynamic-content">
							<div className="title">{getTransactionTypeLabel(transactionType)} Items</div>
						</div>
					),
				},
				body: () => (
					<div style={{ minHeight: '200px' }}>
						{loading ? (
							<Loading />
						) : error ? (
							<Typography variant="body2" style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
								Error loading items: {error.message}
							</Typography>
						) : items.length === 0 ? (
							<EmptyList
								title="No items found"
								subtitle="Transaction items will appear here"
							/>
						) : (
							<List className="core-list">
								{items.map((item: any, index: number) => {
									const quantityValue = getQuantityValue(item, transactionType);
									const quantityDisplay = quantityValue !== null && quantityValue !== undefined
										? parseFloat(String(quantityValue)).toFixed(4)
										: '-';

									return (
										<React.Fragment key={item.ID}>
											<ListItem>
												<ListItemText
													primary={
														<span className="flex-space desc">
															{item.menuItemName || '-'}
															<span className="desc right-info">
																{quantityDisplay} {item.uomName ? `/${item.uomName}` : ''}
															</span>
														</span>
													}
													secondary={
														<>
															<div className="desc">
																{getQuantityLabel(transactionType)}: {quantityDisplay}
																{item.uomName && ` ${item.uomName}`}
															{transactionType === StockTransactionType.StockOut || transactionType === StockTransactionType.StockAdjustment ? (
																item.reasonName && (
																	<span className="desc" style={{ marginTop: '4px', marginLeft: '8px' }}>
																		Reason: {item.reasonName}
																	</span>
																)
															) : null}
															{item.remark && (
																<span className="desc" style={{ marginTop: '4px', fontStyle: 'italic', marginLeft: '8px' }}>
																	{item.remark}
																</span>
															)}
															</div>

														</>
													}
												/>
											</ListItem>
											{index < items.length - 1 && <Divider component="li" />}
										</React.Fragment>
									);
								})}
							</List>
						)}
					</div>
				),
				footer: {
					actions: [
						{
							displayText: 'Close',
							props: {
								onClick: onClose,
								variant: 'contained',
								color: 'primary',
							},
						},
					],
				},
			}}
		/>
	);
};

